import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { CHAT_STORAGE_KEYS } from '../constants/storageKeys';
import { logger } from '../utils/logger';
import { prepareMessagesForSessionStorage, restoreMessagesFromSessionStorage } from '../utils/assistantExportContent';
import { useBrowserAuth } from './AuthContext';

const moduleLogger = logger.getModule('ChatContext');

export interface ChatMessage {
  id: string;
  messageId?: number; // Backend message ID for feedback
  content: string;
  sender: string;
  direction: 'incoming' | 'outgoing';
  timestamp: string;
  localTimestamp: string;
  citations?: string[];
  metadata?: Record<string, any>;
}

interface ChatContextType {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  updateMessageMetadata: (messageId: string, updates: Record<string, any>) => void;
  setMessages: (messages: ChatMessage[]) => void;
  clearMessages: () => void;
  updateSessionId: (sessionId: string | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const browserAuth = useBrowserAuth();
  const authenticatedUserId = browserAuth.authenticated ? browserAuth.user?.id || null : null;
  const [messages, setMessagesState] = useState<ChatMessage[]>([]);
  const [cacheOwnerId, setCacheOwnerId] = useState<string | null>(null);
  const previousSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (browserAuth.loading || !authenticatedUserId || cacheOwnerId === authenticatedUserId) {
      return;
    }

    try {
      const storedOwnerId = sessionStorage.getItem(CHAT_STORAGE_KEYS.authenticatedUserId);
      if (storedOwnerId !== authenticatedUserId) {
        moduleLogger.info('Clearing cached chat state after authenticated user changed');
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.messages);
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.sessionId);
        sessionStorage.setItem(CHAT_STORAGE_KEYS.authenticatedUserId, authenticatedUserId);
        previousSessionIdRef.current = null;
        setMessagesState([]);
      } else {
        const storedSessionId = sessionStorage.getItem(CHAT_STORAGE_KEYS.sessionId);
        const storedMessages = sessionStorage.getItem(CHAT_STORAGE_KEYS.messages);

        if (storedMessages && storedSessionId) {
          const restoredMessages = restoreMessagesFromSessionStorage(JSON.parse(storedMessages));
          previousSessionIdRef.current = storedSessionId;
          setMessagesState(restoredMessages);
          moduleLogger.debug('Restored messages from sessionStorage', {
            count: restoredMessages.length,
            sessionId: storedSessionId,
          });
        } else {
          if (storedMessages) {
            moduleLogger.warn('Found orphaned messages in sessionStorage, clearing them');
            sessionStorage.removeItem(CHAT_STORAGE_KEYS.messages);
          }
          previousSessionIdRef.current = storedSessionId;
          setMessagesState([]);
        }
      }
    } catch (error) {
      moduleLogger.error('Failed to restore messages from sessionStorage', error);
      previousSessionIdRef.current = null;
      setMessagesState([]);
    }

    setCacheOwnerId(authenticatedUserId);
  }, [authenticatedUserId, browserAuth.loading, cacheOwnerId]);

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    if (!authenticatedUserId || cacheOwnerId !== authenticatedUserId) {
      return;
    }

    try {
      if (messages.length > 0) {
        sessionStorage.setItem(CHAT_STORAGE_KEYS.messages, JSON.stringify(prepareMessagesForSessionStorage(messages)));
        moduleLogger.debug('Saved messages to sessionStorage', { count: messages.length });
      } else {
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.messages);
        moduleLogger.debug('Cleared messages from sessionStorage');
      }
    } catch (error) {
      moduleLogger.error('Failed to save messages to sessionStorage', error);
    }
  }, [authenticatedUserId, cacheOwnerId, messages]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessagesState(prev => [...prev, message]);
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
    setMessagesState(prev =>
      prev.map(message =>
        message.id === messageId ? { ...message, ...updates } : message
      )
    );
  }, []);

  const updateMessageMetadata = useCallback((messageId: string, updates: Record<string, any>) => {
    setMessagesState(prev =>
      prev.map(message => (
        message.id === messageId
          ? { ...message, metadata: { ...message.metadata, ...updates } }
          : message
      ))
    );
  }, []);

  const setMessages = useCallback((messages: ChatMessage[]) => {
    setMessagesState(messages);
  }, []);

  const clearMessages = useCallback(() => {
    setMessagesState([]);
  }, []);

  const updateSessionId = useCallback((sessionId: string | null) => {
    if (!authenticatedUserId || cacheOwnerId !== authenticatedUserId) {
      return;
    }

    const previousSessionId = previousSessionIdRef.current;
    
    if (sessionId !== previousSessionId) {
      moduleLogger.debug('Session ID changed, clearing messages', { 
        previous: previousSessionId, 
        current: sessionId 
      });
      clearMessages();
      previousSessionIdRef.current = sessionId;
      
      // Clear old messages from sessionStorage and save new session ID
      try {
        // Always clear messages when session changes
        sessionStorage.removeItem(CHAT_STORAGE_KEYS.messages);
        
        if (sessionId) {
          sessionStorage.setItem(CHAT_STORAGE_KEYS.sessionId, sessionId);
          moduleLogger.debug('Saved new session ID to sessionStorage', { sessionId });
        } else {
          sessionStorage.removeItem(CHAT_STORAGE_KEYS.sessionId);
          moduleLogger.debug('Removed session ID from sessionStorage');
        }
      } catch (error) {
        moduleLogger.error('Failed to update sessionStorage on session ID change', error);
      }
    }
  }, [authenticatedUserId, cacheOwnerId, clearMessages]);

  const visibleMessages = cacheOwnerId === authenticatedUserId ? messages : [];

  return (
    <ChatContext.Provider value={{
      messages: visibleMessages,
      addMessage,
      updateMessage,
      updateMessageMetadata,
      setMessages,
      clearMessages,
      updateSessionId,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
